import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { moveArrayElement } from '@ipfabric/utils';
import type { RowDragState } from 'components/LazyRowContainer/LazyRowContainer';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getMouseEventElementCoordinates } from 'utils/events/getMouseEventElementCoordinates';

interface UseDraggableTableProps<TRow> {
  data: TRow[];
  onDrop: (reorderedData: TRow[], fromIndex: number, toIndex: number) => void;
  /** Optional stable identity used to keep drag active across immutable rerenders */
  getRowIdentity?: (row: TRow) => unknown;
}

interface UseDraggableTableResult {
  rowDragState: RowDragState;
  onRowMouseMove: (rowIndex: number, e: React.MouseEvent) => void;
  onRowMouseLeave: () => void;
  onRowDragStart: (rowIndex: number) => void;
}

export const useDraggableTable = <TRow>(
  props: UseDraggableTableProps<TRow>,
): UseDraggableTableResult => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [draggedItem, setDraggedItem] = useState<null | TRow>(null);
  const [dropBeforeIndex, setDropBeforeIndex] = useState<number | null>(null);
  const isIndexInDataRange = (index: number) =>
    index >= 0 && index < props.data.length;
  const isSameDraggedItem = (currentItem: TRow, originalItem: TRow) =>
    props.getRowIdentity
      ? Object.is(
          props.getRowIdentity(currentItem),
          props.getRowIdentity(originalItem),
        )
      : currentItem === originalItem;

  // Finalize drag globally on mouseup so drop still works if pointer leaves table rows.
  const handleMouseUp = useStableCallback(() => {
    if (draggedIndex !== null) {
      if (dropBeforeIndex !== null) {
        // 1) Compute final destination for moveArrayElement.
        // dropBeforeIndex means "insert before this slot"; dragging downward shifts index by one.
        const draggedToIndex =
          dropBeforeIndex > draggedIndex
            ? dropBeforeIndex - 1
            : dropBeforeIndex;
        if (
          draggedToIndex !== draggedIndex &&
          draggedItem !== null &&
          isIndexInDataRange(draggedIndex) &&
          isIndexInDataRange(draggedToIndex) &&
          // 2) Reject stale drags when data changed and source row no longer matches the grabbed row.
          isSameDraggedItem(props.data[draggedIndex]!, draggedItem)
        ) {
          // 3) Commit only real moves; noop and invalid moves are intentionally ignored.
          props.onDrop(
            moveArrayElement(props.data, draggedIndex, draggedToIndex),
            draggedIndex,
            draggedToIndex,
          );
        }
      }
      // 4) Mouseup always ends drag state, even if no drop was committed.
      setDraggedIndex(null);
      setDraggedItem(null);
      setDropBeforeIndex(null);
    }
  });

  // Keep exactly one window listener for the hook lifetime.
  useEffect(() => {
    const ac = new AbortController();
    window.addEventListener('mouseup', handleMouseUp, { signal: ac.signal });
    return () => {
      ac.abort();
    };
  }, []);

  const onRowMouseMove = useStableCallback(
    (rowIndex: number, e: React.MouseEvent) => {
      if (draggedIndex !== null) {
        const element = e.currentTarget as HTMLElement;
        const dropY = getMouseEventElementCoordinates(e, element).y;
        // Upper half => drop before this row, lower half => after this row.
        const dropAfter = dropY > element.offsetHeight / 2;
        setDropBeforeIndex(rowIndex + (dropAfter ? 1 : 0));
      }
    },
  );

  const onRowMouseLeave = useStableCallback(() => {
    if (dropBeforeIndex !== null) {
      setDropBeforeIndex(null);
    }
  });

  const onRowDragStart = useStableCallback((rowIndex: number) => {
    const item = isIndexInDataRange(rowIndex)
      ? (props.data[rowIndex] ?? null)
      : null;
    setDraggedIndex(rowIndex);
    setDraggedItem(item);
  });

  return {
    rowDragState: { draggedIndex, dropBeforeIndex },
    onRowMouseMove,
    onRowMouseLeave,
    onRowDragStart,
  };
};
